import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RepairItem } from "@/types";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { generateQRCodeDataURL } from "@/utils/qrCodeUtils";
import { formatDistanceToNow } from "date-fns";
import { Phone, FileText, CreditCard, AlertCircle, Hash } from "lucide-react";

interface RepairCardProps {
  repair: RepairItem;
  showQRCode?: boolean; // Make QR code optional for performance
}

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  inProgress: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  returned: "bg-purple-100 text-purple-800 border-purple-200",
};

const paymentStatusColors = {
  paid: "bg-green-100 text-green-800 border-green-200",
  partial: "bg-blue-100 text-blue-800 border-blue-200",
  unpaid: "bg-red-100 text-red-800 border-red-200",
};

const RepairCard: React.FC<RepairCardProps> = ({
  repair,
  showQRCode = false,
}) => {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);
  const { t, i18n } = useTranslation();

  // Only generate QR code when explicitly requested for performance
  useEffect(() => {
    if (!showQRCode) return;

    const generateQR = async () => {
      setQrLoading(true);
      try {
        const qrDataUrl = await generateQRCodeDataURL(repair.id);
        setQrCode(qrDataUrl);
      } catch (error) {
        console.error("Failed to generate QR code:", error);
      } finally {
        setQrLoading(false);
      }
    };

    generateQR();
  }, [repair.id, showQRCode]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: "TND",
    }).format(amount);
  };

  const getStatusText = (status: string) => {
    return t(`repair.status.${status}`);
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="text-lg">{repair.customerName}</CardTitle>
            <p className="text-sm text-gray-500">{repair.customerPhone}</p>
            {repair.ticketNumber && (
              <div className="flex items-center space-x-1 mt-1">
                <Hash className="h-3 w-3 text-gray-400" />
                <span className="text-xs text-gray-500 font-medium">
                  {t("repair.ticketNumber")}: {repair.ticketNumber}
                </span>
              </div>
            )}
          </div>
          <Badge className={statusColors[repair.status]}>
            {getStatusText(repair.status)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pb-2">
        <div className="flex justify-between items-start">
          <div className="space-y-3 flex-1">
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-gray-500" />
              <span className="text-sm">{repair.phoneModel}</span>
            </div>

            <div className="flex items-start space-x-2">
              <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
              <p className="text-sm text-gray-600 line-clamp-2">
                {repair.problemDescription}
              </p>
            </div>

            <div className="flex items-center space-x-2">
              <CreditCard className="h-4 w-4 text-gray-500" />
              <div className="flex flex-wrap gap-2 items-center">
                {repair.priceModifications &&
                repair.priceModifications.length > 0 ? (
                  <div className="flex items-center gap-1">
                    <span className="text-sm font-medium">
                      {formatCurrency(
                        repair.repairPrice +
                          repair.priceModifications.reduce(
                            (sum, mod) => sum + mod.amount,
                            0
                          )
                      )}
                    </span>
                    <span
                      className="inline-flex items-center cursor-help"
                      title={`${t("repair.priceModified")}\n${t(
                        "repair.initialPrice"
                      )}: ${formatCurrency(
                        repair.repairPrice
                      )}\n${repair.priceModifications
                        .map(
                          (mod) =>
                            `${mod.reason}: ${formatCurrency(mod.amount)}`
                        )
                        .join("\n")}`}
                    >
                      <AlertCircle className="h-3 w-3 text-blue-600 ml-1" />
                    </span>
                  </div>
                ) : (
                  <span className="text-sm font-medium">
                    {formatCurrency(repair.repairPrice)}
                  </span>
                )}
                <div className="flex flex-wrap gap-2">
                  <Badge className={paymentStatusColors[repair.paymentStatus]}>
                    {t(`repair.payment.${repair.paymentStatus}`)}
                  </Badge>

                  {/* Payment confirmation badge */}
                  {repair.paymentStatus === "paid" &&
                    (repair.paymentConfirmed ? (
                      <Badge className="bg-green-100 text-green-800 border-green-200 flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                        {t("repair.confirmed")}
                      </Badge>
                    ) : (
                      <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 flex items-center gap-1">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <circle cx="12" cy="12" r="10" />
                          <line x1="12" y1="8" x2="12" y2="12" />
                          <line x1="12" y1="16" x2="12.01" y2="16" />
                        </svg>
                        {t("repair.needsConfirmation")}
                      </Badge>
                    ))}
                </div>

                {repair.downPayment > 0 && (
                  <span className="text-xs text-gray-500">
                    {t("repair.downPayment")}:{" "}
                    {formatCurrency(repair.downPayment)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {showQRCode && (
            <div className="ml-4">
              {qrLoading ? (
                <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : qrCode ? (
                <img
                  src={qrCode}
                  alt="QR Code"
                  className="w-24 h-24 object-contain"
                />
              ) : (
                <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded text-xs text-gray-500">
                  QR Error
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="pt-2 flex justify-between">
        <div className="text-xs text-gray-500">
          {t("repair.addedAgo", {
            time: formatDistanceToNow(new Date(repair.createdAt), {
              addSuffix: true,
            }),
          })}
        </div>
        <Button asChild variant="ghost" size="sm">
          <Link to={`/repair/${repair.id}`}>{t("repair.viewDetails")}</Link>
        </Button>
      </CardFooter>
    </Card>
  );
};

export default RepairCard;
